import type { LabConfig, TestResult } from '../types';
import { computeStats, measureFixedWork, now } from '../utils';
import { runGpuComposite } from './D_gpu_composite';

const GROUP = 'D_gpu';

function getGL(): WebGLRenderingContext | WebGL2RenderingContext | null {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 512;
  const gl2 = canvas.getContext('webgl2', { antialias: false, powerPreference: 'high-performance' });
  if (gl2) return gl2;
  const gl1 = canvas.getContext('webgl', { antialias: false, powerPreference: 'high-performance' });
  return gl1;
}

function gpuBarrier(gl: WebGLRenderingContext | WebGL2RenderingContext): void {
  // Force the GPU to complete the queued work before returning.
  if (typeof (gl as WebGL2RenderingContext).finish === 'function') {
    (gl as WebGL2RenderingContext).finish();
    return;
  }
  // Fallback for environments where finish is hidden by the typings/driver.
  const pixel = new Uint8Array(4);
  gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
}

function compileProgram(
  gl: WebGLRenderingContext | WebGL2RenderingContext,
  vs: string,
  fs: string,
  tfVaryings?: string[]
): WebGLProgram {
  const compile = (type: number, source: string): WebGLShader => {
    const s = gl.createShader(type)!;
    gl.shaderSource(s, source);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      const msg = gl.getShaderInfoLog(s) || 'compile error';
      gl.deleteShader(s);
      throw new Error(msg);
    }
    return s;
  };

  const v = compile(gl.VERTEX_SHADER, vs);
  const f = compile(gl.FRAGMENT_SHADER, fs);
  const p = gl.createProgram()!;
  gl.attachShader(p, v);
  gl.attachShader(p, f);
  if (tfVaryings && 'transformFeedbackVaryings' in gl) {
    (gl as WebGL2RenderingContext).transformFeedbackVaryings(p, tfVaryings, (gl as WebGL2RenderingContext).SEPARATE_ATTRIBS);
  }
  gl.linkProgram(p);
  gl.deleteShader(v);
  gl.deleteShader(f);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    const msg = gl.getProgramInfoLog(p) || 'link error';
    gl.deleteProgram(p);
    throw new Error(msg);
  }
  return p;
}

const FULLSCREEN_VS = `
attribute vec2 a_pos;
void main() {
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

const TF_VS = `#version 300 es
in vec2 a_pos;
out vec2 v_pos;
void main() {
  gl_Position = vec4(a_pos, 0.0, 1.0);
  v_pos = a_pos * 2.0;
}
`;

const TF_FS = `#version 300 es
precision mediump float;
in vec2 v_pos;
out vec4 fragColor;
void main() {
  fragColor = vec4(v_pos, 0.0, 1.0);
}
`;

const ALU_FS = `
precision mediump float;
uniform float u_time;
void main() {
  vec2 uv = gl_FragCoord.xy / 512.0;
  vec3 c = vec3(0.0);
  for (int i = 0; i < 500; i++) {
    float f = float(i);
    uv = abs(uv) - 0.5;
    float r = length(uv);
    float a = atan(uv.y, uv.x);
    float angle = a * 3.0 + u_time + f * 0.1;
    // Anti-dead-code: each iteration depends on previous color output.
    uv.x = r * cos(angle) + c.r * 0.001;
    uv.y = r * sin(angle) + c.g * 0.001;
    c.r += sin(uv.x * 5.0 + f) * 0.02;
    c.g += cos(uv.y * 4.0 + f) * 0.02;
    c.b += sin(r * 10.0 + f) * 0.02;
  }
  gl_FragColor = vec4(c, 1.0);
}
`;

function setupFullScreenQuad(gl: WebGLRenderingContext | WebGL2RenderingContext) {
  const buf = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  return buf;
}

function runFragmentAlu(gl: WebGLRenderingContext | WebGL2RenderingContext, program: WebGLProgram, buf: WebGLBuffer): void {
  gl.useProgram(program);
  const pos = gl.getAttribLocation(program, 'a_pos');
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.enableVertexAttribArray(pos);
  gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);
  const timeLoc = gl.getUniformLocation(program, 'u_time');
  gl.viewport(0, 0, 512, 512);
  // Lightweight fragment stress (D1.2 tuned down for stability).
  for (let i = 0; i < 4; i++) {
    gl.uniform1f(timeLoc, (now() + i) / 1000);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }
  gpuBarrier(gl);
}

function runTextureUpload(gl: WebGLRenderingContext | WebGL2RenderingContext, data: Uint8Array, size: number): void {
  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
  gpuBarrier(gl);
  gl.deleteTexture(tex);
}

function runVertexThroughput(gl: WebGLRenderingContext | WebGL2RenderingContext, program: WebGLProgram, verts: Float32Array, mode: 'transform_feedback' | 'points_fallback'): void {
  const count = verts.length / 2;
  const buf = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.useProgram(program);
  const pos = gl.getAttribLocation(program, 'a_pos');
  gl.enableVertexAttribArray(pos);
  gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);

  if (mode === 'transform_feedback' && 'createTransformFeedback' in gl) {
    const gl2 = gl as WebGL2RenderingContext;
    const tf = gl2.createTransformFeedback()!;
    const outBuf = gl2.createBuffer()!;
    gl2.bindBuffer(gl2.ARRAY_BUFFER, outBuf);
    gl2.bufferData(gl2.ARRAY_BUFFER, verts.byteLength, gl2.STREAM_DRAW);
    gl2.bindBuffer(gl2.ARRAY_BUFFER, null);

    gl2.bindTransformFeedback(gl2.TRANSFORM_FEEDBACK, tf);
    gl2.bindBufferBase(gl2.TRANSFORM_FEEDBACK_BUFFER, 0, outBuf);
    gl2.enable(gl2.RASTERIZER_DISCARD);
    gl2.beginTransformFeedback(gl2.POINTS);
    gl2.drawArrays(gl2.POINTS, 0, count);
    gl2.endTransformFeedback();
    gl2.disable(gl2.RASTERIZER_DISCARD);
    gl2.bindTransformFeedback(gl2.TRANSFORM_FEEDBACK, null);

    gpuBarrier(gl2);
    gl2.deleteBuffer(outBuf);
    gl2.deleteTransformFeedback(tf);
  } else {
    gl.drawArrays(gl.POINTS, 0, count);
    gpuBarrier(gl);
  }

  gl.deleteBuffer(buf);
}

function runFramebufferPingPong(gl: WebGLRenderingContext | WebGL2RenderingContext, program: WebGLProgram, quad: WebGLBuffer): void {
  const size = 512;
  const texA = gl.createTexture()!;
  const texB = gl.createTexture()!;
  const fboA = gl.createFramebuffer()!;
  const fboB = gl.createFramebuffer()!;

  for (const tex of [texA, texB]) {
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  }

  gl.useProgram(program);
  const pos = gl.getAttribLocation(program, 'a_pos');
  gl.bindBuffer(gl.ARRAY_BUFFER, quad);
  gl.enableVertexAttribArray(pos);
  gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);

  for (let pass = 0; pass < 4; pass++) {
    for (let i = 0; i < 8; i++) {
      const fbo = i % 2 === 0 ? fboA : fboB;
      const tex = i % 2 === 0 ? texA : texB;
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
      gl.viewport(0, 0, size, size);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
  }

  gpuBarrier(gl);
  gl.deleteTexture(texA);
  gl.deleteTexture(texB);
  gl.deleteFramebuffer(fboA);
  gl.deleteFramebuffer(fboB);
}

function compileComplexShader(gl: WebGLRenderingContext | WebGL2RenderingContext, timeoutMs: number): Promise<number> {
  const fs = `
precision mediump float;
void main() {
  vec4 c = vec4(0.0);
  for (int i = 0; i < 200; i++) {
    c += vec4(float(i)) / 255.0;
  }
  gl_FragColor = c;
}
`;
  const t0 = now();

  const compile = new Promise<number>((resolve, reject) => {
    try {
      const p = compileProgram(gl, FULLSCREEN_VS, fs);
      gl.deleteProgram(p);
      resolve(now() - t0);
    } catch (err) {
      reject(err);
    }
  });

  const timeout = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error('shader compile timeout')), timeoutMs);
  });

  return Promise.race([compile, timeout]);
}

export async function runGpuTests(config: LabConfig): Promise<TestResult[]> {
  const t0 = now();
  const tests: TestResult[] = [];
  const isProd = config.mode === 'prod';

  if (!config.enableWebGL) {
    tests.push({
      name: 'gpu_disabled',
      group: GROUP,
      success: false,
      durationMs: 0,
      error: 'WebGL disabled in config',
    });
    return tests;
  }

  const gl = getGL();
  if (!gl) {
    tests.push({
      name: 'webgl_init',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: 'WebGL not supported',
    });
    return tests;
  }

  // Always run the lightweight composite score; in prod it is the only GPU test.
  try {
    const composite = runGpuComposite();
    tests.push({
      name: 'gpu_composite_score',
      group: GROUP,
      success: !composite.error,
      durationMs: composite.totalTime,
      metadata: composite.error
        ? { error: composite.error }
        : {
            shaderScore: composite.shaderScore,
            textureScore: composite.textureScore,
            vertexScore: composite.vertexScore,
            framebufferScore: composite.framebufferScore,
            compositeScore: composite.compositeScore,
          },
    });
  } catch (err) {
    tests.push({
      name: 'gpu_composite_score',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  if (isProd) {
    return tests;
  }

  const runs = 5;
  const warmup = 1;

  try {
    const program = compileProgram(gl, FULLSCREEN_VS, ALU_FS);
    // Transform Feedback is disabled for now: GLSL 3.0 varyings do not link
    // reliably across WebGL2 drivers in this environment. Falls back to POINTS.
    const tfProgram: WebGLProgram | null = null;
    const quad = setupFullScreenQuad(gl);

    try {
      const samples = measureFixedWork(() => runFragmentAlu(gl, program, quad), { runs, warmup });
      tests.push({
        name: 'fragment_alu',
        group: GROUP,
        success: true,
        durationMs: samples.reduce((a, b) => a + b, 0),
        samples,
        stats: computeStats(samples),
      });
    } catch (err) {
      tests.push({
        name: 'fragment_alu',
        group: GROUP,
        success: false,
        durationMs: now() - t0,
        error: err instanceof Error ? err.message : String(err),
      });
    }

    try {
      const size = 1024;
      const texData = new Uint8Array(size * size * 4);
      for (let i = 0; i < texData.length; i += 4) {
        texData[i] = i % 256;
        texData[i + 1] = (i * 2) % 256;
        texData[i + 2] = (i * 3) % 256;
        texData[i + 3] = 255;
      }
      const samples = measureFixedWork(() => runTextureUpload(gl, texData, size), { runs, warmup });
      const stats = computeStats(samples);
      tests.push({
        name: 'texture_upload',
        group: GROUP,
        success: true,
        durationMs: samples.reduce((a, b) => a + b, 0),
        samples,
        stats,
        metadata: {
          sizePx: size,
          mbPerSecond: (size * size * 4 / 1024 / 1024) / (stats.median / 1000),
        },
      });
    } catch (err) {
      tests.push({
        name: 'texture_upload',
        group: GROUP,
        success: false,
        durationMs: now() - t0,
        error: err instanceof Error ? err.message : String(err),
      });
    }

    try {
      const count = 200_000;
      const verts = new Float32Array(count * 2);
      for (let i = 0; i < verts.length; i++) verts[i] = (i % 100) / 50 - 1;
      const tfProgram = 'createTransformFeedback' in gl
        ? compileProgram(gl, TF_VS, TF_FS)
        : null;
      const mode: 'transform_feedback' | 'points_fallback' = tfProgram ? 'transform_feedback' : 'points_fallback';
      const samples = measureFixedWork(
        () => runVertexThroughput(gl, tfProgram || program, verts, mode),
        { runs, warmup }
      );
      tests.push({
        name: 'vertex_throughput',
        group: GROUP,
        success: true,
        durationMs: samples.reduce((a, b) => a + b, 0),
        samples,
        stats: computeStats(samples),
        metadata: { mode },
      });
      if (tfProgram) gl.deleteProgram(tfProgram);
    } catch (err) {
      tests.push({
        name: 'vertex_throughput',
        group: GROUP,
        success: false,
        durationMs: now() - t0,
        error: err instanceof Error ? err.message : String(err),
      });
    }

    try {
      const samples = measureFixedWork(() => runFramebufferPingPong(gl, program, quad), { runs, warmup });
      tests.push({
        name: 'framebuffer_pingpong',
        group: GROUP,
        success: true,
        durationMs: samples.reduce((a, b) => a + b, 0),
        samples,
        stats: computeStats(samples),
      });
    } catch (err) {
      tests.push({
        name: 'framebuffer_pingpong',
        group: GROUP,
        success: false,
        durationMs: now() - t0,
        error: err instanceof Error ? err.message : String(err),
      });
    }

    try {
      const times: number[] = [];
      const compileRuns = isProd ? 3 : 10;
      const timeoutMs = 5000;
      for (let i = 0; i < compileRuns; i++) {
        times.push(await compileComplexShader(gl, timeoutMs));
      }
      tests.push({
        name: 'shader_compile_time',
        group: GROUP,
        success: true,
        durationMs: times.reduce((a, b) => a + b, 0),
        samples: times,
        stats: computeStats(times),
      });
    } catch (err) {
      tests.push({
        name: 'shader_compile_time',
        group: GROUP,
        success: false,
        durationMs: now() - t0,
        error: err instanceof Error ? err.message : String(err),
      });
    }

    if (!isProd) {
      try {
        const driftSamples: number[] = [];
        for (let i = 0; i < 10; i++) {
          const s0 = now();
          runFragmentAlu(gl, program, quad);
          const s1 = now();
          driftSamples.push(s1 - s0);
        }
        const first = driftSamples[0];
        const last = driftSamples[driftSamples.length - 1];
        tests.push({
          name: 'thermal_drift',
          group: GROUP,
          success: true,
          durationMs: driftSamples.reduce((a, b) => a + b, 0),
          samples: driftSamples,
          stats: computeStats(driftSamples),
          ratios: {
            last_to_first: last / (first || 1),
          },
        });
      } catch (err) {
        tests.push({
          name: 'thermal_drift',
          group: GROUP,
          success: false,
          durationMs: now() - t0,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }

    gl.deleteBuffer(quad);
    gl.deleteProgram(program);
  } catch (err) {
    tests.push({
      name: 'webgl_setup',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  return tests;
}
